import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { AIRCRAFT_TYPES, DEFAULT_OPTION } from 'constants';
import { AircraftPayload } from 'models';
import { getUTCDateTime } from 'utils';

import {
  aircraftPayloadListGET,
  aircraftPayloadsArchivePOST,
  aircraftPayloadsArchiveGET,
} from 'libs/api';

import { Alert, Row, Col, Button } from 'react-bootstrap';
import DatePicker from 'components/inputs/DatePicker';
import Select from 'components/inputs/Select';
import SkyPulseTable from 'components/SkyPulseTable';
import TailFilterOverlay from 'components/TailFilterOverlay';
import LoadingSpinner from 'components/LoadingSpinner';

import styles from './styles.module.css';

const idPrefix = 'AircraftPayloads';

const AircraftPayloads = (props) => {
  const { aircraftMap } = props;
  const availableFleets = [DEFAULT_OPTION].concat(
    AIRCRAFT_TYPES.getSortedEnumerationItems().map((item) => ({
      name: AIRCRAFT_TYPES[item.id].name,
      value: item.id,
    })),
  );

  // loading layers
  const [isLoading, setIsLoading] = useState(false);

  // aircraft/fleet options
  const [availableAircraft, setAvailableAircraft] = useState([]);

  // filters
  const [selectedAircraft, setSelectedAircraft] = useState([]);
  const [selectedFleet, setSelectedFleet] = useState(null);
  const [startDt, setStartDt] = useState(null);
  const [endDt, setEndDt] = useState(null);
  const [minDate, setMinDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30)),
  );

  // timings
  const endOfDayMs = 24 * 60 * 60 * 1000 - 1000;

  // tail overlay
  const tailButtonRef = useRef();
  const [tailFilter, setTailFilter] = useState({});
  const [tailFilterOverlayCoordinates, setTailFilterOverlayCoordinates] =
    useState(null);

  // payloads
  const [availablePayloads, setAvailablePayloads] = useState([]);

  // alerts
  const [alertMessage, setAlertMessage] = useState('');

  // clear stale alerts
  useEffect(() => {
    if (alertMessage === '') {
      return;
    }
    setTimeout(() => {
      setAlertMessage('');
    }, 10000);
  }, [alertMessage]);

  useEffect(() => {
    let aircraftCollection = [];
    if (!aircraftMap) {
      return;
    } else {
      aircraftCollection = Array.from(aircraftMap.values());
    }

    const filteredAircraft = !selectedFleet
      ? aircraftCollection
      : aircraftCollection.filter((aircraft) => {
          return aircraft.aircraft_type.id === selectedFleet;
        });

    setAvailableAircraft(filteredAircraft);
  }, [aircraftMap, selectedFleet]);

  // callbacks

  // responsible for setting the selected aircraft from the tail filter overlay
  const onTailFilterDone = (filters) => {
    setTailFilter(filters);

    const filteredAircraft = Object.entries(filters)
      .map(([aircraftId, selected]) => {
        return selected ? aircraftMap.get(aircraftId) : null;
      })
      .filter((aircraft) => aircraft !== null);

    setSelectedAircraft(filteredAircraft);
    setTailFilterOverlayCoordinates(null);
  };

  // responsible for finding the current position of a given ref
  const onFilterPickerOpen = (ref) => {
    const { left, top } = ref.current.getBoundingClientRect();
    return { x: left, y: top };
  };

  const handleGetPayloadList = async () => {
    setIsLoading(true);
    setAvailablePayloads([]);

    // fetch the available payloads
    const response = await aircraftPayloadListGET(
      selectedAircraft.map(({ id }) => id),
      startDt / 1000,
      endDt / 1000,
    ).catch((e) => {
      setIsLoading(false);
      if (e.status === 404) {
        setAlertMessage('No payloads found.');
      } else {
        setAlertMessage(
          'There was a problem fetching payloads, please try again shortly.',
        );
      }
    });

    const {
      data: { result = [] },
    } = response;

    const availablePayloads = result.map((item) => new AircraftPayload(item));
    setAvailablePayloads(availablePayloads);
    setIsLoading(false);
  };

  const handleDownload = async (downloadAll, aircraftPayloadId = null) => {
    setIsLoading(true);

    const payloadIds = downloadAll
      ? availablePayloads.map(({ id }) => id)
      : [aircraftPayloadId];

    const {
      data: { result: exportKey },
    } = await aircraftPayloadsArchivePOST(payloadIds).catch(() => {
      setAlertMessage('Unable to create aircraft payload archive.');
      setIsLoading(false);
    });

    try {
      const {
        data: { result: secureUrl },
      } = await waitForArchive(exportKey);

      if (secureUrl) {
        const anchor = document.createElement('a');
        anchor.setAttribute('href', secureUrl);
        anchor.setAttribute('target', '_blank');
        anchor.click();
      }
    } catch (e) {
      setAlertMessage(e);
    } finally {
      setIsLoading(false);
    }
  };

  const waitForArchive = (exportKey) => {
    const maximumRetries = 12;
    return new Promise((resolve, reject) => {
      let retryCount = 0;
      const refreshIntervalId = setInterval(async () => {
        retryCount++;

        return await aircraftPayloadsArchiveGET(exportKey)
          .then((result) => {
            clearInterval(refreshIntervalId);
            resolve(result);
          })
          .catch(() => {
            if (retryCount > maximumRetries) {
              clearInterval(refreshIntervalId);
              reject('Unable to retrieve aircraft payload archive.');
            }
          });
      }, 5000);
    });
  };

  return (
    <>
      <Row className={styles.root} noGutters>
        <Col>
          <Row className={styles.title} noGutters>
            <Col className="title_1">Aircraft Payloads</Col>
          </Row>

          {alertMessage !== '' && (
            <Row>
              <Col>
                <Alert
                  variant="info"
                  onClose={() => {
                    setAlertMessage('');
                  }}
                  dismissible
                >
                  <div className={styles.centerText}>{alertMessage}</div>
                </Alert>
              </Col>
            </Row>
          )}

          <Row
            className={styles.filterRow}
            data-cy-id={`${idPrefix}-Filters`}
            noGutters
          >
            <Col>
              <Row>
                <Col md="auto">
                  <Select
                    idPrefix={`${idPrefix}-Fleet`}
                    data-cy-id={`${idPrefix}-FiltersPanel-FleetSelect`}
                    label="Fleet: "
                    options={availableFleets}
                    value={selectedFleet}
                    onChange={(e) => {
                      setSelectedFleet(e.target.value);
                      setSelectedAircraft([]);
                    }}
                    classname={styles.filterControl}
                  />
                </Col>

                <Col md="auto">
                  Tail
                  <Button
                    ref={tailButtonRef}
                    onClick={() =>
                      setTailFilterOverlayCoordinates(
                        onFilterPickerOpen(tailButtonRef),
                      )
                    }
                    className={classnames(
                      'button_reg',
                      'ml-2',
                      styles.tailButton,
                      styles.filterControl,
                    )}
                  >
                    {selectedAircraft.length < 1 ? (
                      '--'
                    ) : (
                      <>{selectedAircraft.map(({ tail }) => tail).join(', ')}</>
                    )}
                  </Button>
                </Col>

                <Col md="auto">
                  <DatePicker
                    idPrefix={`${idPrefix}-DateStart`}
                    label="Start Date: "
                    value={startDt}
                    onChange={(timestamp) => {
                      setMinDate(timestamp);
                      setStartDt(timestamp);
                      setEndDt(null);
                    }}
                    minDate={minDate}
                    maxDate={Date.now()}
                  />
                </Col>

                <Col md="auto">
                  <DatePicker
                    idPrefix={`${idPrefix}-DateEnd`}
                    label="End Date: "
                    value={endDt}
                    onChange={(timestamp) => {
                      const endOfDay = timestamp + endOfDayMs;
                      setEndDt(endOfDay);
                    }}
                    minDate={minDate}
                    maxDate={Date.now()}
                  />
                </Col>
              </Row>
            </Col>

            <Col md="auto">
              <Button
                className={classnames('button_foc', styles.filterControl)}
                data-cy-id={`${idPrefix}-LoadButton`}
                disabled={isLoading || !selectedAircraft || !startDt || !endDt}
                onClick={handleGetPayloadList}
              >
                Fetch Payloads
              </Button>
            </Col>
          </Row>

          {!isLoading ? null : <LoadingSpinner />}

          {!availablePayloads.length ? null : (
            <Row className={styles.dataContainer} noGutters>
              <Col className="h-100">
                <SkyPulseTable
                  title={
                    <Button
                      className="button_foc"
                      data-cy-id={`${idPrefix}-DownloadButton`}
                      disabled={isLoading || !availablePayloads.length}
                      onClick={() => handleDownload(true)}
                    >
                      Download All Payloads
                    </Button>
                  }
                  titleClassName={styles.titleContainer}
                  columns={[
                    {
                      name: 'ID',
                      key: 'id',
                      size: 'small',
                    },
                    {
                      name: 'Aircraft Tail',
                      key: 'tail',
                      sortable: true,
                    },
                    {
                      name: 'Created At',
                      key: 'created',
                      sortable: true,
                    },
                    {
                      name: 'Transmission At',
                      key: 'transmission_time',
                      sortable: true,
                    },
                    {
                      name: 'Queued At',
                      key: 'queue_time',
                      sortable: true,
                    },
                    {
                      name: 'Processed At',
                      key: 'process_time',
                      sortable: true,
                    },
                    {
                      name: 'Status',
                      key: 'status',
                      sortable: true,
                    },
                  ]}
                  rows={availablePayloads.map((payload, index) => {
                    return {
                      id: (
                        <div
                          key={index}
                          className={'skypulsetable_toggle_button'}
                          data-clickable-id={payload.id}
                        >
                          {payload.id}
                        </div>
                      ),
                      tail: payload.tail,
                      created: getUTCDateTime(payload.created),
                      transmission_time: getUTCDateTime(
                        payload.transmission_time,
                      ),
                      queue_time: getUTCDateTime(payload.queue_time),
                      process_time: getUTCDateTime(payload.process_time),
                      status: payload.status.value,
                    };
                  })}
                  onRowClick={(_id, aircraftPayloadId = null) => {
                    if (!aircraftPayloadId) {
                      return;
                    }
                    handleDownload(false, aircraftPayloadId);
                  }}
                  isLoading={isLoading}
                  showHeaders={true}
                  className={styles.logTable}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <TailFilterOverlay
        position={tailFilterOverlayCoordinates}
        isOpen={tailFilterOverlayCoordinates !== null}
        onCancel={() => setTailFilterOverlayCoordinates(null)}
        onDone={onTailFilterDone}
        filters={tailFilter}
        tails={availableAircraft
          .map((aircraft) => {
            return {
              id: aircraft.id,
              value: `${aircraft.tail} (${aircraft.registration || 'NA'})`,
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value))}
      />
    </>
  );
};

export default AircraftPayloads;
